import { BottomSheetModal } from '@gorhom/bottom-sheet';
import {
  TransferDestinationOption,
  TransferSourceOption,
} from 'features/feeBasedInstantTransfer/adapters/transferOptionsAdapter';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AccessibilityInfo, StyleSheet, View } from 'react-native';
import AmplitudeMask from 'shared/components/AmplitudeMask/AmplitudeMask';
import BottomSheet from 'shared/components/vxl/BottomSheet/BottomSheet';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import TransferField, {
  TransferFieldAccessoryProps,
} from 'shared/components/vxl/fields/TransferField/TransferField';
import ListItem from 'shared/components/vxl/ListItem/ListItem';
import { toTagIconKey, toTagType } from 'shared/components/vxl/Tag/Tag.helpers';
import TitleMdText from 'shared/components/vxl/typography/primary/title/TitleMdText';
import { isWeb } from 'shared/constants/platform';
import { VXLComponentId } from 'shared/renderer/types';
import theme from 'shared/styles/theme';

type TransferOption = TransferSourceOption | TransferDestinationOption;

const OPTION_SECONDARY_MAX_LINES = 3;

interface TransferOptionSelectorProps<T extends TransferOption> {
  label: string;
  sheetTitle: string;
  options: T[];
  selectedOption?: T;
  isDisabled?: boolean;
  isReadOnly?: boolean;
  isLoading?: boolean;
  onSelect: (option: T) => void;
  onOpen?: () => void;
}

function getOptionIconKey(option: TransferOption): 'BANK' | 'CARD' | undefined {
  if (option.kind === 'externalDebitCard') {
    return 'CARD';
  }

  return option.iconUrl ? undefined : 'BANK';
}

function getAvatarAccessory(
  option: TransferOption | undefined,
  id: string,
): TransferFieldAccessoryProps {
  return {
    id,
    name: VXLComponentId.ICON_AVATAR,
    props: {
      variant: 'MD' as const,
      iconKey: option ? getOptionIconKey(option) : 'BANK',
      imageSource: option?.iconUrl ? { uri: option.iconUrl } : undefined,
    },
  };
}

function getOptionAccessibilityLabel(option: TransferOption) {
  return [
    option.primaryText,
    option.secondaryText,
    option.tag?.text,
    option.isSelectable ? undefined : option.unavailableReason,
  ]
    .filter(Boolean)
    .join(', ');
}

const focusTitle = (title: View | null) => {
  if (!title) {
    return;
  }

  if (isWeb) {
    title.focus();
  } else {
    AccessibilityInfo.sendAccessibilityEvent(title, 'focus');
  }
};

function TransferOptionSelector<T extends TransferOption>({
  label,
  sheetTitle,
  options,
  selectedOption,
  isDisabled = false,
  isReadOnly = false,
  isLoading = false,
  onSelect,
  onOpen,
}: TransferOptionSelectorProps<T>) {
  const { t: translatedText } = useTranslation('feeBasedInstantTransfer', {
    keyPrefix: 'entry',
  });
  const bottomSheetRef = useRef<BottomSheetModal>(null);
  const triggerRef = useRef<View>(null);
  const [isOpen, setIsOpen] = useState(false);
  const isSelectorDisabled = isDisabled || isLoading;

  useEffect(() => {
    if (!isOpen || !isWeb) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        bottomSheetRef.current?.dismiss();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const handleOpen = () => {
    if (!isSelectorDisabled && !isReadOnly) {
      onOpen?.();
      setIsOpen(true);
      bottomSheetRef.current?.present();
    }
  };

  const restoreFocus = () => {
    if (isWeb) {
      triggerRef.current?.focus();
    } else if (triggerRef.current) {
      AccessibilityInfo.sendAccessibilityEvent(triggerRef.current, 'focus');
    }
  };

  const handleSelect = (option: T) => {
    bottomSheetRef.current?.dismiss();
    onSelect(option);
  };

  const handleDismiss = () => {
    setIsOpen(false);
    restoreFocus();
  };

  return (
    <>
      <TransferField
        focusRef={triggerRef}
        avatarAccessory={getAvatarAccessory(
          selectedOption,
          `${label}-transfer-option`,
        )}
        isCollapsed={!isOpen}
        isDisabled={isSelectorDisabled}
        labelText={label}
        onPress={handleOpen}
        primaryText={
          selectedOption?.primaryText ?? translatedText('selectAccount')
        }
        secondaryText={selectedOption?.secondaryText}
        showChevron={!isReadOnly}
      />
      <BottomSheet
        ref={bottomSheetRef}
        allowScrolling
        contentContainerStyle={styles.sheetContent}
        onDismiss={handleDismiss}
        setIsBottomSheetOpened={setIsOpen}
      >
        <View
          ref={focusTitle}
          accessible
          accessibilityRole="header"
          style={styles.sheetTitle}
          tabIndex={-1}
        >
          <TitleMdText numberOfLines={0}>{sheetTitle}</TitleMdText>
        </View>
        {options.map((option) => (
          <View
            key={`${option.kind}-${option.id}`}
            style={styles.optionContainer}
          >
            <AmplitudeMask blockStyleMask style={styles.optionMask}>
              <ListItem
                accessibilityLabel={getOptionAccessibilityLabel(option)}
                isDisabled={!option.isSelectable}
                isSelected={
                  option.id === selectedOption?.id &&
                  option.kind === selectedOption.kind
                }
                leadingAccessory={{
                  id: `${option.kind}-${option.id}`,
                  name: VXLComponentId.ICON_AVATAR,
                  props: {
                    variant: 'MD',
                    iconKey: getOptionIconKey(option),
                    imageSource: option.iconUrl
                      ? { uri: option.iconUrl }
                      : undefined,
                  },
                }}
                onPress={() => handleSelect(option)}
                primaryCopy={option.primaryText}
                primaryNumberOfLines={0}
                secondaryCopy={option.secondaryText}
                secondaryNumberOfLines={OPTION_SECONDARY_MAX_LINES}
                showsChevron={false}
                trailingAccessory={
                  option.tag
                    ? {
                        id: option.tag.id,
                        name: VXLComponentId.TAG,
                        props: {
                          text: option.tag.text,
                          type: toTagType(option.tag.tagColor),
                          iconKey: toTagIconKey(option.tag.icon),
                        },
                      }
                    : undefined
                }
              />
            </AmplitudeMask>
            <View style={styles.divider}>
              <HairLineDivider />
            </View>
          </View>
        ))}
      </BottomSheet>
    </>
  );
}

const styles = StyleSheet.create({
  divider: {
    paddingHorizontal: theme.spacing.m,
  },
  optionContainer: {
    width: '100%',
  },
  optionMask: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  sheetContent: {
    width: '100%',
  },
  sheetTitle: {
    paddingBottom: theme.spacing.s,
    paddingHorizontal: theme.spacing.m,
  },
});

export default TransferOptionSelector;
